import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { getDismissed, setDismissed as sessionSetDismissed } from 'luxPlus/storage/renewalBanner'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import FormatLoyaltyBonusPoints from 'loyalty/components/Formatters/FormatLoyaltyBonusPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS } from 'loyalty/constants/loyaltyBonusPoints'
import * as Analytics from 'analytics/analytics'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import { fetchProductSubscription } from 'actions/LuxPlusActions'

function LuxPlusRenewalBanner() {
  const intl = useIntl()
  const [dismissed, setDismissed] = useState(getDismissed())
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchProductSubscription())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRenewal = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'manual_renewal_purchase',
      action: 'add_to_cart_lux_plus_standalone',
      category: LUXURY_PLUS.ANALYTICS_CATEGORY,
      type: 'interaction',
    })
    dispatch(purchaseLuxPlus({ hasJoinFee: false }))
  }, [dispatch])

  const renewalStatus = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.renewalStatus)
  const hasAutoRenewal = renewalStatus === 'ALLOW_RENEWAL'

  const dismiss = useCallback(() => {
    setDismissed(true)
    sessionSetDismissed()
  }, [])

  if (dismissed) return null

  const renewNowLabel = intl.formatMessage({
    id: 'JyT48gJbdG2hk-m51t-s',
    defaultMessage: 'Renew now',
    description: 'Primary action button text to renew LuxPlus+ membership',
  })
  const manageMembershipLabel = intl.formatMessage({
    id: 'CzduFvXxOE3r00bbd3f1',
    defaultMessage: 'Manage membership',
    description: 'Secondary action button text to manage LuxPlus+ membership',
  })

  return <PageBanner
    kind="critical"
    onDismiss={dismiss}
    primaryActionButtonProps={{
      onClick: handleRenewal,
      children: renewNowLabel,
    }}
    secondaryActionButtonProps={{
      to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`,
      children: manageMembershipLabel,
    }}
  >
    {hasAutoRenewal && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="5zTPW-cLU2cPDt6Flw-f"
      defaultMessage="<b>Your {programName} Membership failed to renew due to a payment issue.</b> Renew now to receive <b>{statusCredits} and {bonusPoints}</b> with {loyaltyProgram}."
      description="Critical banner when LuxPlus+ renewal fails, with loyalty bonus incentive"
      values={{
        programName: LUXURY_PLUS.PROGRAM_NAME,
        statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
        bonusPoints: <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
        loyaltyProgram: getLoyaltyProgramDisplayName(),
        b: (chunks) => <b>{chunks}</b>,
      }}
    />}
    {hasAutoRenewal && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="jNCAiYJPSkkD6gqWPtUJ"
      defaultMessage="<b>Your {programName} Membership failed to renew due to a payment issue.</b> Your benefits are on hold until payment is completed."
      description="Critical banner when LuxPlus+ renewal fails, benefits on hold"
      values={{
        programName: LUXURY_PLUS.PROGRAM_NAME,
        b: (chunks) => <b>{chunks}</b>,
      }}
    />}
    {!hasAutoRenewal && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="ZJp0JswhxDhO2uYaPiXa"
      defaultMessage="<b>Your {programName} Membership will expire soon.</b> Renew now to receive <b>{statusCredits} and {bonusPoints}</b> with {loyaltyProgram}."
      description="Critical banner when LuxPlus+ membership expiring, with loyalty bonus incentive"
      values={{
        programName: LUXURY_PLUS.PROGRAM_NAME,
        statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
        bonusPoints: <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
        loyaltyProgram: getLoyaltyProgramDisplayName(),
        b: (chunks) => <b>{chunks}</b>,
      }}
    />}
    {!hasAutoRenewal && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="6-A4ggZniVMtCIeLPAyl"
      defaultMessage="<b>Your {programName} Membership will expire soon.</b> Your benefits are on hold until payment is completed."
      description="Critical banner when LuxPlus+ membership expiring, benefits on hold"
      values={{
        programName: LUXURY_PLUS.PROGRAM_NAME,
        b: (chunks) => <b>{chunks}</b>,
      }}
    />}
  </PageBanner>
}

export default LuxPlusRenewalBanner
